import { plains256 } from './zones';
import { plainsChain } from './plainsOpening';
import { CH2_QUESTS } from './story/ch2';

/**
 * What an objective counts. The fights (`kill`, `block`, `guardBreak`, `interrupt`) are counted by the World; the
 * rest only by the 256 m plains' chain (systems/plainsStory.ts): `talk` someone, `visit` a place, `meditate` at a
 * place, train a `neigong` (its id in `target`), win a `duelWin` bout, choose a `sect` (or the wanderer path).
 */
export type ObjectiveType =
  | 'kill'
  | 'block'
  | 'guardBreak'
  | 'interrupt'
  | 'talk'
  | 'visit'
  | 'meditate'
  | 'neigong'
  | 'duelWin'
  | 'sect'
  // First-hour fixes (docs/design/quests/2026-10-11-first-hour-f1-f4.md): plains chain only, counted by systems/plainsStory.ts.
  | 'lockForm' // `target`: the lock move (e.g. 'bs-reed') the player lands as a locked form (ท่าล็อกเป้า)
  | 'route' // `target`: a route id in data/plainsOpening.ts ROUTES; one count per checkpoint reached in order
  | 'escort' // `target`: an escort id in ESCORTS; done when the cart reaches the end of its path
  // Origin chains (systems/originFull.ts): a blow on the hero passed by a dash (`dodge`) or answered by a counter (`counter`).
  | 'dodge'
  | 'counter';

export interface Objective {
  readonly type: ObjectiveType;
  /** Monster id for kill objectives. */
  readonly target?: string;
  readonly count: number;
  readonly label: string;
}

export interface QuestDef {
  readonly id: string;
  readonly title: string;
  readonly offer: string;
  readonly remind: string;
  readonly done: string;
  readonly objectives: readonly Objective[];
  readonly rewards: {
    readonly cultivation: number;
    readonly gold: number;
    readonly arts?: readonly string[];
    readonly neigong?: readonly string[];
    readonly title?: string;
  };
  readonly spawnsBoss?: boolean;
  /**
   * Who offers it and takes it back when not the elder (the 256 m plains' chain): their name and where they stand, for
   * the quest strip ("คุยกับ… ที่…"). Left out: the elder at the stone.
   */
  readonly giver?: { readonly name: string; readonly where: string };
}

/** The elder's chain (the 72 m plains and every zone but the 256 m plains). */
export const BASE_QUESTS: readonly QuestDef[] = [
  {
    id: 'three-forms',
    title: 'สามกระบวนท่าแห่งยุทธ์',
    offer:
      'ผู้มาใหม่ ก่อนจะถือกระบี่ต้องเข้าใจสามกระบวนท่าก่อน โจมตีชนะท่าทำลาย ท่าทำลายชนะการตั้งรับ การตั้งรับชนะการโจมตี ' +
      'ไปขอซ้อมมือกับศิษย์คู่ซ้อมที่ลานฝึก ข้ามสะพานลานไปฝั่งตะวันออก หรือหาโจรภูเขาก็ได้ เมื่อมันง้างดาบ ให้คลิกขวาค้างตั้งรับ เมื่อมันตั้งรับ ให้ใช้ท่า 穿 ทำลาย เมื่อมันชาร์จท่าทำลาย ให้โจมตีขัดจังหวะ',
    remind:
      'ลานฝึกอยู่ริมถนนฝั่งตะวันออก เลยสะพานลานไป · อ่านไอคอนเหนือหัวศัตรู: 攻 ให้ตั้งรับ (คลิกขวาค้าง) · 守 ให้ใช้ท่าทำลาย (2) · 破 ให้โจมตีขัดจังหวะ (1)',
    done: 'เจ้าอ่านจังหวะได้แล้ว นี่คือรากฐานของวิชาทั้งปวง',
    objectives: [
      { type: 'block', count: 3, label: 'ตั้งรับการโจมตี' },
      { type: 'guardBreak', count: 1, label: 'ทำลายการตั้งรับ' },
      { type: 'interrupt', count: 1, label: 'ขัดจังหวะท่าทำลาย' },
    ],
    rewards: { cultivation: 240, gold: 40 },
  },
  {
    id: 'bandits',
    title: 'โจรภูเขาบุกลานศิลา',
    offer:
      'โจรพวกนี้ปล้นผู้เดินทางมานานพอแล้ว ปราบมันห้าคน แล้วข้าจะมอบคัมภีร์ฝ่ามือเมฆาคล้อยให้ ยุทธภพไม่มีอาชีพตายตัว ผู้ใดใฝ่ฝึก ผู้นั้นได้วิชา',
    remind: 'โจรภูเขาซุ่มอยู่บนเนินโจรทางใต้ของลาน ที่หยานหยูให้ออกประตูใต้ลาน เดินตามถนนลงไปสุดทางแล้วเลี้ยวไปทางตะวันตกเฉียงใต้',
    done: 'รับคัมภีร์ฝ่ามือเมฆาคล้อยและจิตเมฆาไร้รูปไป เปิดแผงวิชา (K) เพื่อสลับวิชา และแผงกำลังภายใน (N)',
    objectives: [{ type: 'kill', target: 'bandit', count: 5, label: 'โจรภูเขา' }],
    rewards: { cultivation: 400, gold: 60, arts: ['cloud-palm'], neigong: ['cloud-heart'] },
  },
  {
    id: 'wolves',
    title: 'เสียงหอนใต้แสงจันทร์',
    offer:
      'หมาป่าเงาจันทร์ในป่าต้นน้ำทางเหนือดุร้ายผิดปกติ ราวกับมีบางสิ่งปลุกพวกมัน กำจัดหกตัว ใช้แต้มฝึกปรือขัดเกลาวิชาก่อนออกไปจะดีกว่า',
    remind:
      'หมาป่าเงาจันทร์อยู่ในป่าต้นน้ำทางเหนือ ที่หยานหยูให้เดินขึ้นเหนือตามถนนฝั่งตะวันตกของแม่น้ำจนสุดทาง หมาป่าไม่ตั้งรับ โจมตีได้เต็มที่',
    done: 'กลิ่นปราณมารติดตัวพวกมัน ข้าเกรงว่าเขากลับมาแล้ว รับแต้มฝึกปรือนี้ไปขัดเกลาวิชาให้พร้อม',
    objectives: [{ type: 'kill', target: 'wolf', count: 6, label: 'หมาป่าเงาจันทร์' }],
    rewards: { cultivation: 700, gold: 90 },
  },
  {
    id: 'black-sword',
    title: 'ปีศาจกระบี่ทมิฬ',
    offer:
      'ปีศาจกระบี่ทมิฬ ศิษย์ทรยศผู้ขโมยกระบี่เล่มที่สิบจากใต้ศิลา ตื่นขึ้นที่ลานเลือดบนผาแดงทางตะวันออก เมื่อก่อนมันมีชื่อว่าฮั่วอู๋ซิน… ศิษย์ของข้าเอง ' +
      'เมื่อมันรวมปราณ วงสีแดงจะปรากฏใต้เท้า วงนั้นคือท่าทำลาย ตั้งรับไม่ได้ ให้กด Space ใช้วิชาตัวเบาหลบ',
    remind:
      'ลานเลือดอยู่บนผาแดงทางตะวันออก ที่หยานหยูให้ข้ามสะพานลานแล้วเดินตามถนนฝั่งตะวันออกไปสุดทาง ระวังวงปราณสีแดง ต้องหลบ ตั้งรับไม่ได้',
    done: 'เจ้าทำได้ อู๋ซินได้พักเสียที… นับแต่นี้ยุทธภพจะขนานนามเจ้าว่า ผู้พิชิตกระบี่ทมิฬ แต่กระบี่สีดำไม่ได้อยู่กับศพของเขา',
    objectives: [{ type: 'kill', target: 'boss', count: 1, label: 'ปีศาจกระบี่ทมิฬ' }],
    rewards: { cultivation: 1200, gold: 300, title: 'ผู้พิชิตกระบี่ทมิฬ' },
    spawnsBoss: true,
  },
  {
    id: 'monks',
    title: 'ศิษย์ทรยศวัดร้าง',
    offer:
      'ที่วัดหินระฆังแห่งเขาพันบันไดมีวัดร้าง พระนอกรีตกลุ่มหนึ่งยึดไว้ จากลานศิลาเก้ากระบี่ ไปทางวงวาร์ปทิศตะวันตก พวกมันตั้งรับเก่ง อย่าฟันตรงๆ ให้ใช้ท่าทำลายตัดการ์ดก่อน ปราบหกรูป แล้วข้าจะเล่าเรื่องกระบี่เล่มที่สิบให้ฟัง',
    remind:
      'พระนอกรีตอยู่ที่วัดหินระฆังแห่งเขาพันบันได (วงวาร์ปทิศตะวันตกของลานศิลา) พวกมันตั้งรับบ่อย ใช้ท่าทำลาย (กรอบสีส้ม)',
    done: 'พระพวกนั้นรับใช้จอมมารพันกระบี่ เจ้าแห่งกระบี่เล่มที่สิบ มันซ่อนตัวอยู่ทางเหนือ แต่เจ้ายังไม่พร้อม',
    objectives: [{ type: 'kill', target: 'monk', count: 6, label: 'พระนอกรีต' }],
    rewards: { cultivation: 900, gold: 120 },
  },
  {
    id: 'bears',
    title: 'หมีดำลงจากเขา',
    offer:
      'หมีดำภูเขาแห่งเขาพันบันไดถูกปราณมารปลุกให้คลุ้มคลั่ง (วัดหินระฆัง หรือผาดาบชาด) มันตีหนักและขย้ำทำลายการ์ด อย่ายืนรับ ให้หลบด้วยวิชาตัวเบาแล้วฟันสวน ปราบห้าตัว',
    remind:
      'หมีดำอยู่ที่วัดหินระฆังแห่งเขาพันบันไดและผาดาบชาด ท่าขย้ำของมันทำลายการตั้งรับ ให้หลบ (Ctrl) แทนการตั้งรับ',
    done: 'แม้แต่สัตว์ป่ายังถูกปราณมารครอบงำ จอมมารกำลังสะสมพลัง',
    objectives: [{ type: 'kill', target: 'bear', count: 5, label: 'หมีดำภูเขา' }],
    rewards: { cultivation: 1000, gold: 140 },
  },
  {
    id: 'jiangshi',
    title: 'ผีกระโดดคืนเดือนมืด',
    offer:
      'ที่สุสานใต้ภูเขายอดหมอก ศพที่ถูกปลุกด้วยปราณมารลุกขึ้นกระโดดไปมา เล็บของมันมีพิษ อย่าให้พิษทบซ้อน ปราบแปดตัว',
    remind:
      'ผีกระโดดอยู่ที่ภูเขายอดหมอก (ช่องเขาตะวันตกของหยานหยู หรือวงวาร์ปทิศเหนือของวัดหินระฆัง) ระวังพิษสะสม',
    done: 'ศพพวกนั้นถูกปลุกด้วยกระบี่เล่มที่สิบ ผู้ที่ถือมันกำลังเรียกกองทัพ',
    objectives: [{ type: 'kill', target: 'jiangshi', count: 8, label: 'ผีกระโดด' }],
    rewards: { cultivation: 1200, gold: 160 },
  },
  {
    id: 'shadows',
    title: 'เงาที่ตามล่าศิลา',
    offer:
      'ทางโคมเหล็กกำลังไล่ล่านักฆ่าเงาดำและโจรมีดบินที่ซุ่มอยู่รอบค่าย พวกมันคือหูตาของจอมมาร ไปช่วยตัดหูตามันเสียก่อน',
    remind:
      'นักฆ่าเงาดำและโจรมีดบินอยู่รอบค่ายทางโคมเหล็ก (วงวาร์ปทิศเหนือของภูเขายอดหมอก) · โจรมีดบินมีบ้างในค่ายโจรทางตะวันตกเฉียงใต้ของลานศิลา',
    done: 'จอมมารตาบอดแล้ว ถึงเวลาที่เจ้าจะขึ้นเหนือไปพบมัน',
    objectives: [
      { type: 'kill', target: 'assassin', count: 5, label: 'นักฆ่าเงาดำ' },
      { type: 'kill', target: 'knifer', count: 4, label: 'โจรมีดบิน' },
    ],
    rewards: { cultivation: 1400, gold: 200 },
  },
  {
    id: 'thousand-swords',
    title: 'จอมมารพันกระบี่',
    offer:
      'จอมมารพันกระบี่รออยู่ที่ลานจอมมาร สุดถนนทางเหนือ ฝนพันกระบี่ของมันตกเป็นวงกว้าง ออกจากวงแดงให้ทัน ไปรวมพลกับจอมยุทธ์คนอื่นก็ได้ มันแข็งแกร่งเกินคนเดียว',
    remind: 'ลานจอมมารอยู่สุดถนนทางเหนือ ออกจากวงแดงของฝนพันกระบี่ให้ทัน',
    done: 'กระบี่เล่มที่สิบแตกเป็นเสี่ยงๆ แต่ปราณของมันยังไม่สลาย มันจะกลับมาอีก และทุกครั้งจะทิ้งกระบี่มารไว้ให้ผู้ชนะ',
    objectives: [{ type: 'kill', target: 'worldboss', count: 1, label: 'จอมมารพันกระบี่' }],
    rewards: { cultivation: 3000, gold: 600 },
  },
];

/** The 256 m plains' chain (data/plainsOpening.ts): origin A's first hour, then the old quests re-homed. */
export const PLAINS_QUESTS: readonly QuestDef[] = [...plainsChain(BASE_QUESTS), ...CH2_QUESTS];

const chain = (): readonly QuestDef[] => (plains256() ? PLAINS_QUESTS : BASE_QUESTS);

/**
 * The quest chain in force: the elder's (`BASE_QUESTS`), or with the 256 m plains on (`plains256()`, data/zones.ts)
 * the plains chain (`PLAINS_QUESTS`). Read through, as an array, every time it is used, so the switch may be set at
 * any point before a World is made (as data/carriage.ts and data/safeZones.ts follow it).
 */
export const QUESTS: readonly QuestDef[] = new Proxy([] as QuestDef[], {
  get: (_t, k) => {
    const a = chain();
    const v: unknown = Reflect.get(a, k, a);
    return typeof v === 'function' ? (v as (...args: unknown[]) => unknown).bind(a) : v;
  },
  has: (_t, k) => Reflect.has(chain(), k),
  ownKeys: () => Reflect.ownKeys(chain()),
  getOwnPropertyDescriptor: (_t, k) => Reflect.getOwnPropertyDescriptor(chain(), k),
});

export const FINAL_DIALOG =
  'ยุทธภพยังกว้างใหญ่ ฝึกปรือต่อไป แล้ววันหนึ่งเจ้าจะได้รู้ความลับของกระบี่เล่มที่สิบ';

/**
 * The wuxia village's starter quest (client v1 hub; kept out of `QUESTS` so the other zones' chains are untouched):
 * the boars at the training yard, then the world boss lands (`spawnsBoss`; flow in src/village/starter.ts).
 */
export const STARTER_QUEST: QuestDef = {
  id: 'village-boars',
  title: 'ปราบหมูป่าเขี้ยวเหล็ก',
  offer: 'ศิษย์ใหม่แห่งสำนัก: หมูป่าเขี้ยวเหล็กบุกลานฝึกฝั่งตะวันตก ไปปราบมันเสีย',
  remind: 'ลานฝึกอยู่ทางตะวันตกของประตูสำนัก',
  done: 'ปราบหมูป่าเขี้ยวเหล็กครบแล้ว',
  objectives: [{ type: 'kill', target: 'boar-starter', count: 5, label: 'หมูป่าเขี้ยวเหล็ก' }],
  rewards: { cultivation: 0, gold: 0 },
  spawnsBoss: true,
};
